'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { BookOpen, Eye, UserRoundPlus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { APP_ROUTES, AUTH_ROUTES } from '@/app/routes';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import { useJoinRoute } from '@/hooks/useJoinRoute/useJoinRoute';
import { LANDING_HERO_SECTION_ID, LANDING_NEXT_SECTION_ID } from '@/templates/Public/Landing/Landing.constants';
import { HeaderSocialLinks } from '../Header/Header';
import { HeaderButtonSignIn } from '../HeaderButtonSignIn/HeaderButtonSignIn';

export const HeaderHome = ({ ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const router = useRouter();
  const pathname = usePathname();
  const joinRoute = useJoinRoute();
  const [showJoinButton, setShowJoinButton] = React.useState(false);

  const isLandingPage = pathname === '/';
  const isLogoutPage = pathname === AUTH_ROUTES.LOGOUT;

  React.useEffect(() => {
    setShowJoinButton(false);

    if (!isLandingPage) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const heroSection = document.getElementById(LANDING_HERO_SECTION_ID);

    if (!heroSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.find(({ target }) => target === heroSection);

        if (entry) {
          setShowJoinButton(!entry.isIntersecting);
        }
      },
      {
        rootMargin: '-30% 0px -35% 0px',
        threshold: 0,
      },
    );

    observer.observe(heroSection);

    return () => {
      observer.disconnect();
    };
  }, [isLandingPage]);

  const handleJoin = () => {
    router.push(joinRoute);
  };

  const handleLearn = () => {
    if (isLandingPage) {
      document.getElementById(LANDING_NEXT_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    router.push(`/#${LANDING_NEXT_SECTION_ID}`);
  };

  const handleExplore = () => {
    router.push(APP_ROUTES.HOME);
  };

  // Landing and logout share the navbar from the design: Learn / Explore (desktop) + Sign in.
  // The social links live in the landing hero footer instead (see HomeFooter).
  const learnExploreButtons = (
    <>
      <Button
        id="header-learn-btn"
        data-testid="header-learn-btn"
        variant="outline"
        onClick={handleLearn}
        className="hidden gap-2 md:inline-flex"
      >
        <BookOpen className="size-4" />
        {'Learn'}
      </Button>
      <Button
        id="header-explore-btn"
        data-testid="header-explore-btn"
        variant="outline"
        onClick={handleExplore}
        className="hidden gap-2 md:inline-flex"
      >
        <Eye className="size-4" />
        {'Explore'}
      </Button>
    </>
  );

  if (isLogoutPage) {
    return (
      <Container className="flex-1 flex-row items-center justify-end gap-3" {...props}>
        {learnExploreButtons}
        <HeaderButtonSignIn />
      </Container>
    );
  }

  return (
    <Container className="flex-1 flex-row items-center justify-end gap-3" {...props}>
      {isLandingPage ? learnExploreButtons : <HeaderSocialLinks />}
      <AnimatePresence initial={false}>
        {showJoinButton && (
          <motion.div
            className="mr-1 flex shrink-0 items-center overflow-hidden"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <div className="flex shrink-0 items-center px-2">
              <Button
                id="header-join-btn"
                variant="brand"
                onClick={handleJoin}
                className="size-10 shrink-0 gap-0 px-0 whitespace-nowrap sm:h-10 sm:w-auto sm:gap-2 sm:px-4"
              >
                <UserRoundPlus className="size-4" />
                <span className="sr-only sm:not-sr-only">{'Join'}</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <HeaderButtonSignIn />
    </Container>
  );
};
